import React, { useState } from 'react';
import {
  Box,
  VStack,
  Textarea,
  Text,
  Button,
  Heading,
  HStack,
  Divider,
  useColorModeValue,
  Grid,
  GridItem
} from '@chakra-ui/react';

/**
 * Component for managing customer notes and displaying lead information
 */
const NewKBCustomerNotes = ({ notes, setNotes, onSave, lead }) => {
  const [isSaving, setIsSaving] = useState(false);
  
  // Color mode values
  const bgColor = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.800', 'white');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');
  
  // Safe string conversion function for rendering any field
  const safeRender = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return value.toString();
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') {
      // Handle specific object types safely
      if (value.name !== undefined) return value.name;
      if (value.displayName !== undefined) return value.displayName;
      if (value.label !== undefined) return value.label;
      if (value.title !== undefined) return value.title;
      if (value.value !== undefined) return safeRender(value.value);
      
      // Last resort if none of the above properties exist
      return 'Complex Value';
    }
    return 'Unknown Value';
  };
  
  const handleSave = async () => {
    setIsSaving(true);
    await onSave(notes);
    setIsSaving(false);
  };
  
  return (
    <VStack spacing={4} align="stretch" h="100%">
      {/* Lead Information */}
      {lead && (
        <Box p={3} borderWidth="1px" borderRadius="md" bg={bgColor}>
          <Heading size="xs" mb={3}>Lead Information</Heading>
          <Grid templateColumns="repeat(2, 1fr)" gap={3}>
            {/* Basic Information */}
            {lead.pointOfContact && (
              <GridItem>
                <Text fontSize="xs" fontWeight="bold" color={textColor}>Point of Contact</Text>
                <Text fontSize="sm">{safeRender(lead.pointOfContact)}</Text>
              </GridItem>
            )}
            
            {lead.phone && (
              <GridItem>
                <Text fontSize="xs" fontWeight="bold" color={textColor}>Phone</Text>
                <Text fontSize="sm">{safeRender(lead.phone)}</Text>
              </GridItem>
            )}
            
            {/* Wedding Details */}
            {lead.eventDates && lead.eventDates.length > 0 && (
              <GridItem>
                <Text fontSize="xs" fontWeight="bold" color={textColor}>Wedding Date</Text>
                <Text fontSize="sm">{new Date(lead.eventDates[0]).toLocaleDateString()}</Text>
              </GridItem>
            )}
            
            {lead.eventType && (
              <GridItem>
                <Text fontSize="xs" fontWeight="bold" color={textColor}>Event Type</Text>
                <Text fontSize="sm">{safeRender(lead.eventType)}</Text>
              </GridItem>
            )}
            
            {lead.guestCapacity && (
              <GridItem>
                <Text fontSize="xs" fontWeight="bold" color={textColor}>Guest Count</Text>
                <Text fontSize="sm">{safeRender(lead.guestCapacity)}</Text>
              </GridItem>
            )}
            
            {/* Budget Information */}
            {lead.budget && lead.budget.amount && (
              <GridItem>
                <Text fontSize="xs" fontWeight="bold" color={textColor}>Budget</Text>
                <Text fontSize="sm">
                  {safeRender(lead.budget.currency)} {lead.budget.amount ? lead.budget.amount.toLocaleString() : ''}
                </Text>
              </GridItem>
            )}
            
            {/* Location Information */}
            {lead.locations && lead.locations.cities && lead.locations.cities.length > 0 && (
              <GridItem>
                <Text fontSize="xs" fontWeight="bold" color={textColor}>Location</Text>
                <Text fontSize="sm">
                  {Array.isArray(lead.locations.cities) ? lead.locations.cities.map(safeRender).join(', ') : safeRender(lead.locations.cities)}
                </Text>
              </GridItem>
            )}
            
            {lead.venues && lead.venues.length > 0 && (
              <GridItem>
                <Text fontSize="xs" fontWeight="bold" color={textColor}>Venues</Text>
                <Text fontSize="sm">
                  {Array.isArray(lead.venues) ? lead.venues.map(safeRender).join(', ') : safeRender(lead.venues)}
                </Text>
              </GridItem>
            )}
            
            {/* Source and Status */}
            {lead.leadSource && (
              <GridItem>
                <Text fontSize="xs" fontWeight="bold" color={textColor}>Lead Source</Text>
                <Text fontSize="sm">{safeRender(lead.leadSource)}</Text>
              </GridItem>
            )}
            
            {lead.status && (
              <GridItem>
                <Text fontSize="xs" fontWeight="bold" color={textColor}>Status</Text>
                <Text fontSize="sm">{safeRender(lead.status)}</Text>
              </GridItem>
            )}
            
            {/* Next Follow-up */}
            {lead.nextFollowUpTime && (
              <GridItem>
                <Text fontSize="xs" fontWeight="bold" color={textColor}>Next Follow-up</Text>
                <Text fontSize="sm">{new Date(lead.nextFollowUpTime).toLocaleString()}</Text>
              </GridItem>
            )}
          </Grid>
        </Box>
      )}
      
      {/* Customer Notes */}
      <Box 
        flex={1} 
        borderWidth="1px" 
        borderRadius="md" 
        p={3}
        bg={bgColor}
      >
        <HStack justify="space-between" mb={2}>
          <Heading size="xs">Customer Notes</Heading>
          <Button
            size="xs"
            colorScheme="blue"
            onClick={handleSave}
            isLoading={isSaving}
          >
            Save Notes
          </Button>
        </HStack>
        
        <HStack justify="space-between" mb={1} alignItems="center">
          <Text fontSize="xs" color={mutedColor}>
            These notes are for your reference and will be saved with the lead
          </Text>
        </HStack>
        
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Enter customer notes here..."
          size="md"
          rows={10}
          resize="vertical"
          h="calc(100% - 60px)"
          bg={bgColor}
          borderColor={borderColor}
        />
      </Box>
    </VStack>
  );
};

export default NewKBCustomerNotes;
